import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Button, Modal, ProgressBar, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { ToWords } from 'to-words';

import usePolling from 'hooks/usePolling';

import { picklistApi } from 'api';

const POLLING_INTERVAL_MS = 5000;

const getErrorCode = error => {
  const data = error?.response?.data;
  if (!data) return null;
  return [].concat(data.error_code || [])[0] || null;
};

const getErrorDetail = error => {
  const data = error?.response?.data;
  if (!data) return null;
  return [].concat(data.detail || [])[0] || null;
};

const playBeep = (frequency, durationMs) => {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.frequency.value = frequency;
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    gain.gain.value = 0.08;
    oscillator.start();
    setTimeout(() => {
      oscillator.stop();
      ctx.close();
    }, durationMs);
  } catch (e) {
    // Sound is best-effort only
  }
};

const successFeedback = () => {
  playBeep(880, 120);
  navigator.vibrate?.(80);
};

const errorFeedback = () => {
  playBeep(220, 250);
  navigator.vibrate?.([120, 60, 120]);
};

const toWords = new ToWords();

// Speak the cart partition number aloud in English ("One", "Ten") so the
// picker can drop the item without looking at the screen.
const speakPartition = partitionNumber => {
  try {
    if (!window.speechSynthesis || partitionNumber == null) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(
      toWords.convert(partitionNumber),
    );
    utterance.lang = 'en-IN';
    utterance.rate = 1.1;
    utterance.volume = 1;
    window.speechSynthesis.speak(utterance);
  } catch (e) {
    // Voice is best-effort only
  }
};

/**
 * Worker mobile page for batch-code live picking (opened via QR code).
 *
 * A hidden always-focused input receives HID barcode-scanner keystrokes.
 * All progress lives on the server (LivePickRecord) — reopening the page
 * resumes exactly where picking stopped, on any device.
 */
const LivePicking = () => {
  const { picklistId } = useParams();

  const [state, setState] = useState(null);
  const [pageError, setPageError] = useState(null); // {code, message}
  const [initialLoading, setInitialLoading] = useState(true);
  const [started, setStarted] = useState(false);
  const [lastScan, setLastScan] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [scanValue, setScanValue] = useState('');

  const inputRef = useRef(null);
  const wakeLockRef = useRef(null);

  // ---- data ----

  const fetchState = useCallback(async () => {
    try {
      const response = await picklistApi.getLivePickingState(picklistId);
      setState(response);
      setPageError(null);
    } catch (error) {
      const code = getErrorCode(error);
      if (code) {
        setPageError({
          code,
          message: getErrorDetail(error) || 'This picklist is not available.',
        });
      }
    } finally {
      setInitialLoading(false);
    }
  }, [picklistId]);

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  const progress = state?.progress;
  const tasks = useMemo(() => state?.tasks || [], [state]);
  const totalUnits = progress?.total_units || 0;
  const doneUnits = (progress?.picked || 0) + (progress?.skipped || 0);
  const isReceiving = state?.picklist?.picklist_status === 'receiving';
  const isComplete = totalUnits > 0 && progress?.remaining === 0;
  const currentTask = useMemo(
    () => tasks.find(task => task.units?.remaining > 0) || null,
    [tasks],
  );
  const canUndo = (state?.my_last_actions || []).length > 0;
  const cancelledAfterPick = state?.cancelled_after_pick || [];
  const skippedTasks = useMemo(
    () => tasks.filter(task => task.units?.skipped > 0),
    [tasks],
  );

  // R2: fallback strip data derived from the server's my_last_actions, so the
  // strip walks back to the previous scan after an undo (and survives
  // re-entry) without any client-side history.
  const derivedLastScan = useMemo(() => {
    const action = (state?.my_last_actions || []).find(
      item => item.result === 'picked' && item.partition_number != null,
    );
    if (!action) return null;
    const task = tasks.find(item => item.batch_code === action.batch_code);
    return {
      recovered_from_skip: false,
      partition_number: action.partition_number,
      shipment_code: null,
      unit: { product: task?.product, batch_code: action.batch_code },
    };
  }, [state, tasks]);

  usePolling(
    fetchState,
    POLLING_INTERVAL_MS,
    started && isReceiving && !isComplete && isOnline,
  );

  // ---- device: wake lock, online/offline, scanner focus ----

  useEffect(() => {
    const requestWakeLock = async () => {
      try {
        wakeLockRef.current = await navigator.wakeLock?.request('screen');
      } catch (e) {
        // Wake lock is best-effort only
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        requestWakeLock();
      }
    };

    requestWakeLock();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      wakeLockRef.current?.release?.();
    };
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      fetchState();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [fetchState]);

  const refocusInput = useCallback(() => {
    // Keep the hidden input focused so HID scanner keystrokes always land
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  useEffect(() => {
    if (started && !showSkipModal) {
      refocusInput();
    }
  }, [started, showSkipModal, scanning, state, refocusInput]);

  // ---- actions ----

  const handleScan = async code => {
    const batchCode = (code || '').trim();
    if (!batchCode || scanning || !isOnline) return;

    try {
      setScanning(true);
      const response = await picklistApi.livePickingScan(picklistId, batchCode);
      setLastScan(response);
      successFeedback();
      speakPartition(response.partition_number);
      if (response.recovered_from_skip) {
        toast.info('Recovered from skip');
      }
      await fetchState();
    } catch (error) {
      errorFeedback();
      const errorCode = getErrorCode(error);
      switch (errorCode) {
        case 'CODE_NOT_IN_PICKLIST':
          toast.error('Code not in this picklist');
          break;
        case 'BATCH_ALREADY_DONE':
          toast.warning('All units of this batch are already done');
          break;
        case 'PICKLIST_NOT_RECEIVING':
          toast.warning('This picklist is no longer in picking');
          await fetchState();
          break;
        case 'STOCKTAKING_LOCKED':
          // Global interceptor already shows the stocktaking toast
          break;
        default:
          toast.error(getErrorDetail(error) || 'Scan failed');
      }
    } finally {
      setScanning(false);
      refocusInput();
    }
  };

  const handleInputKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const value = scanValue;
      setScanValue('');
      handleScan(value);
    }
  };

  const handleUndo = async () => {
    if (scanning || !canUndo) return;
    try {
      setScanning(true);
      const response = await picklistApi.livePickingUndo(picklistId);
      const undone = response?.undone;
      toast.info(
        undone?.result === 'skipped'
          ? `Undone: skip of ${undone?.batch_code} (${undone?.units} unit(s))`
          : `Undone: partition ${undone?.partition_number} · ${undone?.batch_code}`,
      );
      setLastScan(null);
      await fetchState();
    } catch (error) {
      errorFeedback();
      const errorCode = getErrorCode(error);
      if (errorCode === 'LINE_ALREADY_PACKED') {
        toast.error('This item has already been packed. Undo is not possible.');
      } else if (errorCode === 'NOTHING_TO_UNDO') {
        toast.info('Nothing to undo');
      } else {
        toast.error(getErrorDetail(error) || 'Undo failed');
      }
      await fetchState();
    } finally {
      setScanning(false);
      refocusInput();
    }
  };

  const handleSkipConfirm = async () => {
    if (!currentTask) return;
    try {
      setScanning(true);
      const response = await picklistApi.livePickingSkip(
        picklistId,
        currentTask.product_batch_id,
      );
      toast.warning(
        `Skipped ${response?.skipped_units} unit(s) of ${currentTask.batch_code}`,
      );
      await fetchState();
    } catch (error) {
      errorFeedback();
      toast.error(getErrorDetail(error) || 'Skip failed');
    } finally {
      setScanning(false);
      setShowSkipModal(false);
      refocusInput();
    }
  };

  // ---- render helpers ----

  const renderOfflineBanner = () =>
    !isOnline && (
      <Alert variant="danger" className="rounded-0 mb-0 py-2 text-center fs-10">
        Offline — scanning is paused. Waiting for connection...
      </Alert>
    );

  const renderCancelledBanner = () =>
    cancelledAfterPick.length > 0 && (
      <Alert variant="danger" className="mx-2 mt-2 mb-0 py-2 fs-10">
        <b>Order cancelled after pick — return these items to the shelf:</b>
        {cancelledAfterPick.map(item => (
          <div key={item.shipment_line_id} className="font-monospace">
            Partition {item.partition_number} · {item.product?.sku} (
            {item.batch_code})
          </div>
        ))}
      </Alert>
    );

  const renderProgress = () => (
    <div className="bg-white border-bottom px-3 py-2">
      <div className="d-flex justify-content-between fs-10 text-600 mb-1">
        <span>
          <b className="text-900">{doneUnits}</b> / {totalUnits} units
        </span>
        <span>
          picked {progress?.picked || 0}
          {progress?.skipped ? ` · skipped ${progress.skipped}` : ''}
        </span>
      </div>
      <ProgressBar style={{ height: 8 }}>
        <ProgressBar
          variant="success"
          now={totalUnits ? ((progress?.picked || 0) / totalUnits) * 100 : 0}
          key={1}
        />
        <ProgressBar
          variant="warning"
          now={totalUnits ? ((progress?.skipped || 0) / totalUnits) * 100 : 0}
          key={2}
        />
      </ProgressBar>
    </div>
  );

  const renderLastScan = () => {
    const scan = lastScan || derivedLastScan;
    if (!scan) return null;
    const recovered = scan.recovered_from_skip;
    return (
      <div
        className={`d-flex align-items-center gap-3 mx-2 mt-2 p-2 border rounded-3 ${
          recovered
            ? 'bg-warning-subtle border-warning'
            : 'bg-success-subtle border-success'
        }`}
      >
        <div
          className={`d-flex flex-column align-items-center justify-content-center text-white rounded-3 flex-shrink-0 ${
            recovered ? 'bg-warning' : 'bg-success'
          }`}
          style={{ width: 56, height: 56 }}
        >
          <span style={{ fontSize: 9, letterSpacing: 1 }}>PART</span>
          <span className="fw-bold font-monospace" style={{ fontSize: 26 }}>
            {scan.partition_number}
          </span>
        </div>
        <div className="overflow-hidden">
          <div className="fw-bold fs-10">
            {recovered
              ? `Recovered from skip → partition ${scan.partition_number}`
              : `Put into partition ${scan.partition_number}`}
          </div>
          <div className="fs-10 text-truncate">{scan.unit?.product?.name}</div>
          <div className="fs-11 text-600 font-monospace text-truncate">
            {scan.unit?.batch_code}
            {scan.shipment_code ? ` · ${scan.shipment_code}` : ''}
          </div>
        </div>
      </div>
    );
  };

  const renderTaskCard = () =>
    currentTask && (
      <div className="bg-white border rounded-3 mx-2 mt-2 p-3">
        <div className="d-flex align-items-center gap-2">
          <span className="badge bg-primary font-monospace fs-9">
            {currentTask.shelf?.code}
          </span>
          <span className="fs-11 text-600">
            priority {currentTask.shelf?.priority ?? 0}
          </span>
        </div>
        <div className="fw-bold font-monospace mt-2" style={{ fontSize: 16 }}>
          {currentTask.batch_code}
        </div>
        <div className="fs-10">{currentTask.product?.name}</div>
        <div className="fs-11 text-600 font-monospace">
          {currentTask.product?.sku}
        </div>
        <div className="d-flex justify-content-between align-items-center mt-2">
          <span className="fw-bold" style={{ fontSize: 20 }}>
            {currentTask.units?.remaining}{' '}
            <span className="fs-11 text-600 fw-normal">to scan</span>
          </span>
          <div className="d-flex gap-1">
            {(currentTask.pending_partitions || []).map(partition => (
              <span
                key={partition}
                className="border rounded d-flex align-items-center justify-content-center font-monospace fw-bold fs-10 bg-light"
                style={{ width: 26, height: 26 }}
              >
                {partition}
              </span>
            ))}
          </div>
        </div>
      </div>
    );

  const renderTaskList = () => (
    <div className="mx-2 mt-2 mb-3">
      <div className="fs-11 text-600 text-uppercase mb-1 ms-1">
        All batches (shelf order)
      </div>
      {tasks.map(task => {
        const remaining = task.units?.remaining || 0;
        const skipped = task.units?.skipped || 0;
        const isCurrent = task === currentTask;
        return (
          <div
            key={task.product_batch_id}
            className={`d-flex justify-content-between align-items-center bg-white border rounded-3 px-2 py-1 mb-1 ${
              remaining ? '' : 'opacity-50'
            } ${isCurrent ? 'border-primary' : ''}`}
          >
            <span className="overflow-hidden">
              <span className="fw-semibold font-monospace fs-10 d-block text-truncate">
                {task.batch_code}
              </span>
              <span className="fs-11 text-600 font-monospace">
                {task.shelf?.code}
              </span>
            </span>
            <span className="fs-11 text-end flex-shrink-0">
              {remaining > 0 ? (
                `${remaining} left`
              ) : skipped > 0 ? (
                <span className="text-warning fw-semibold">
                  SKIPPED {skipped}
                </span>
              ) : (
                <span className="text-success fw-semibold">DONE</span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );

  // ---- screens ----

  if (initialLoading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (pageError) {
    return (
      <div className="min-vh-100 d-flex flex-column align-items-center justify-content-center p-4 text-center">
        <h5>
          {pageError.code === 'NOT_BATCH_CODE_PICKLIST'
            ? 'Not a batch-code picklist'
            : 'Batch picking unavailable'}
        </h5>
        <p className="text-600 fs-10">{pageError.message}</p>
        <Button variant="primary" onClick={fetchState}>
          Retry
        </Button>
      </div>
    );
  }

  const picklistCode = state?.picklist?.picklist_code;

  // Complete screen
  if (started && isComplete) {
    return (
      <div
        className="min-vh-100 d-flex flex-column bg-light mx-auto"
        style={{ maxWidth: 480 }}
      >
        {renderOfflineBanner()}
        <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center p-4 text-center">
          <div style={{ fontSize: 52 }}>✅</div>
          <h4 className="mt-2 mb-1">Picking Complete</h4>
          <div className="fs-10 text-600 font-monospace">{picklistCode}</div>

          <div className="d-flex gap-2 my-3 w-100">
            <div className="flex-fill bg-white border rounded-3 py-2">
              <div className="fw-bold text-success fs-8">
                {progress?.picked || 0}
              </div>
              <div className="fs-11 text-600 text-uppercase">Picked</div>
            </div>
            <div className="flex-fill bg-white border rounded-3 py-2">
              <div className="fw-bold text-warning fs-8">
                {progress?.skipped || 0}
              </div>
              <div className="fs-11 text-600 text-uppercase">Skipped</div>
            </div>
            <div className="flex-fill bg-white border rounded-3 py-2">
              <div className="fw-bold fs-8">{progress?.total_partitions}</div>
              <div className="fs-11 text-600 text-uppercase">Partitions</div>
            </div>
          </div>

          {skippedTasks.length > 0 ? (
            <Alert variant="warning" className="w-100 text-start fs-10 py-2">
              <b>Skipped items — tell the packing station:</b>
              {skippedTasks.map(task => (
                <div
                  key={task.product_batch_id}
                  className="d-flex justify-content-between font-monospace"
                >
                  <span>{task.batch_code}</span>
                  <span>
                    {task.shelf?.code} × {task.units?.skipped}
                  </span>
                </div>
              ))}
            </Alert>
          ) : (
            <p className="fs-10 text-600">
              All items picked. Bring the cart to packing.
            </p>
          )}

          <Button
            variant="outline-secondary"
            className="w-100"
            disabled={!canUndo || scanning}
            onClick={handleUndo}
          >
            Undo Last
          </Button>
        </div>
      </div>
    );
  }

  // Start / Resume screen
  if (!started) {
    const isResume = doneUnits > 0;
    return (
      <div
        className="min-vh-100 d-flex flex-column bg-light mx-auto"
        style={{ maxWidth: 480 }}
      >
        {renderOfflineBanner()}
        <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center p-4 text-center">
          <div style={{ fontSize: 48 }}>{isResume ? '⏸️' : '📦'}</div>
          <h4 className="mt-2 mb-1 font-monospace">{picklistCode}</h4>
          <div className="fs-10 text-600">Batch code picking</div>

          {!isReceiving ? (
            <Alert variant="secondary" className="mt-3 fs-10">
              This picklist is not in picking status.
            </Alert>
          ) : (
            <>
              <div className="d-flex gap-2 my-3 w-100">
                {isResume ? (
                  <>
                    <div className="flex-fill bg-white border rounded-3 py-2">
                      <div className="fw-bold text-success fs-8">
                        {progress?.picked || 0}
                      </div>
                      <div className="fs-11 text-600 text-uppercase">
                        Picked
                      </div>
                    </div>
                    <div className="flex-fill bg-white border rounded-3 py-2">
                      <div className="fw-bold text-warning fs-8">
                        {progress?.skipped || 0}
                      </div>
                      <div className="fs-11 text-600 text-uppercase">
                        Skipped
                      </div>
                    </div>
                    <div className="flex-fill bg-white border rounded-3 py-2">
                      <div className="fw-bold fs-8">{progress?.remaining}</div>
                      <div className="fs-11 text-600 text-uppercase">
                        Remaining
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex-fill bg-white border rounded-3 py-2">
                      <div className="fw-bold fs-8">{totalUnits}</div>
                      <div className="fs-11 text-600 text-uppercase">Units</div>
                    </div>
                    <div className="flex-fill bg-white border rounded-3 py-2">
                      <div className="fw-bold fs-8">
                        {progress?.total_partitions}
                      </div>
                      <div className="fs-11 text-600 text-uppercase">
                        Partitions
                      </div>
                    </div>
                    <div className="flex-fill bg-white border rounded-3 py-2">
                      <div className="fw-bold fs-8">{tasks.length}</div>
                      <div className="fs-11 text-600 text-uppercase">
                        Batches
                      </div>
                    </div>
                  </>
                )}
              </div>

              <p className="fs-10 text-600">
                {isResume ? (
                  <>
                    <b>
                      {doneUnits} / {totalUnits} units
                    </b>{' '}
                    already done.
                    <br />
                    Progress is saved — continue where you left off.
                  </>
                ) : (
                  <>
                    Prepare a cart with{' '}
                    <b>{progress?.total_partitions} partitions</b>, then start.
                  </>
                )}
              </p>

              {(progress?.unassigned_units || 0) > 0 && (
                <Alert variant="warning" className="w-100 fs-10 py-2">
                  {progress.unassigned_units} unit(s) have no allocated batch
                  and are excluded. Contact your supervisor.
                </Alert>
              )}

              <Button
                variant="primary"
                className="w-100 py-2"
                onClick={() => setStarted(true)}
              >
                {isResume ? 'Resume Picking' : 'Start Picking'}
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Picking screen
  return (
    <div
      className="min-vh-100 d-flex flex-column bg-light mx-auto"
      style={{ maxWidth: 480 }}
      onClick={refocusInput}
    >
      {renderOfflineBanner()}

      <div className="bg-white border-bottom px-3 py-2 d-flex justify-content-between align-items-center">
        <span className="fw-semibold font-monospace fs-10">{picklistCode}</span>
        <span className="fs-11 text-600">Batch Picking</span>
      </div>

      {renderProgress()}
      {renderCancelledBanner()}
      {renderLastScan()}

      <div className="flex-grow-1 overflow-auto pb-2">
        {renderTaskCard()}
        {renderTaskList()}
        <div className="text-center fs-11 text-500 px-3 pb-2">
          Scan a batch code with the connected scanner
        </div>
      </div>

      {/* Hidden scanner input — always focused, no on-screen keyboard */}
      <input
        ref={inputRef}
        value={scanValue}
        onChange={e => setScanValue(e.target.value)}
        onKeyDown={handleInputKeyDown}
        onBlur={refocusInput}
        inputMode="none"
        autoComplete="off"
        aria-label="Batch code scanner input"
        style={{
          position: 'absolute',
          opacity: 0,
          height: 1,
          width: 1,
          border: 0,
          padding: 0,
        }}
      />

      <div className="bg-white border-top d-flex gap-2 p-2 sticky-bottom">
        <Button
          variant="outline-secondary"
          className="flex-fill py-2"
          disabled={!canUndo || scanning}
          onClick={handleUndo}
        >
          Undo Last
        </Button>
        <Button
          variant="outline-warning"
          className="flex-fill py-2"
          disabled={!currentTask || scanning}
          onClick={() => setShowSkipModal(true)}
        >
          Item Not Found
        </Button>
      </div>

      <Modal
        show={showSkipModal}
        onHide={() => setShowSkipModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-9">Item not found?</Modal.Title>
        </Modal.Header>
        <Modal.Body className="fs-10">
          <b className="font-monospace">{currentTask?.batch_code}</b> —{' '}
          {currentTask?.units?.remaining} remaining unit(s) will be marked as
          SKIPPED. They must be rejected as “not found” at packing. If you find
          the item later, just scan it — the skip is recovered automatically.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSkipModal(false)}>
            Cancel
          </Button>
          <Button
            variant="warning"
            disabled={scanning}
            onClick={handleSkipConfirm}
          >
            Skip {currentTask?.units?.remaining} unit(s)
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LivePicking;
